import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { FORGOT_PASSWORD } from "@/constants/testIds";
import { CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8 bg-white">
      <div className="w-full max-w-md">
        <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-4">{t("auth.forgot.eyebrow")}</div>
        <h1 className="font-display font-black text-4xl text-slate-900 tracking-tighter leading-tight">{t("auth.forgot.title")}</h1>
        <p className="mt-3 text-slate-600 text-sm">{t("auth.forgot.body")}</p>

        {sent ? (
          <div data-testid={FORGOT_PASSWORD.successMessage} className="mt-8 flex items-start gap-3 p-4 rounded-lg border border-blue-200 bg-blue-50/60 text-slate-800">
            <CheckCircle2 className="text-blue-800 shrink-0 mt-0.5" size={18} />
            <div className="text-sm">
              <div className="font-semibold">{t("auth.forgot.successTitle")}</div>
              <div className="mt-1">{t("auth.forgot.successBody")}</div>
          </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-700">{t("auth.forgot.email")}</Label>
              <Input id="email" data-testid={FORGOT_PASSWORD.emailInput} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 h-11 bg-white" />
            </div>
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>}
            <Button type="submit" data-testid={FORGOT_PASSWORD.submitButton} disabled={loading} className="w-full h-11 bg-blue-800 hover:bg-blue-700 text-white rounded-md">
              {loading ? t("auth.forgot.submitting") : t("auth.forgot.submit")}
            </Button>
          </form>
        )}

        <p className="mt-8 text-sm text-slate-600">
          {t("auth.forgot.remembered")}{" "}
          <Link to="/login" className="text-blue-800 font-semibold hover:underline">{t("common.signInArrow")}</Link>
        </p>
      </div>
    </div>
  );
}
